import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import bgImage from '../../assets/Left Content.png';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/Api';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa6';
import { setAuthData } from '../../store/slice/authSlice';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { BeatLoader } from 'react-spinners';

const schema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().min(8).max(32).required(),
});

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(schema) });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onSubmitHandler = async data => {
    const toastId = toast.loading('Logging in...');
    try {
      const response = await api.post('/admin/login', data);

      const { accessToken, data: userData } = response.data.responsePayLoad ?? {};

      if (accessToken && userData?.sub) {
        dispatch(setAuthData({ token: accessToken, userData }));
        toast.success('Login successful!', { id: toastId });
        navigate('/');
      } else {
        toast.error('Login failed: Invalid user data.', { id: toastId });
      }
    } catch (error) {
      console.error('Login Error:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred. Please try again.';
      toast.error(errorMessage, { id: toastId });
    }
  };

  return (
    <div className="flex flex-row overflow-hidden">
      <div className="h-screen col-span-2 bg-center bg-no-repeat bg-auto">
        <img src={bgImage} alt="logo" className="w-full" />
      </div>
      <form
        onSubmit={handleSubmit(onSubmitHandler)}
        className="absolute w-[307px] h-[388px] top-[230px] left-[1002px]">
        <div className="w-[191px] h-[54px] text-center mx-auto bg-clip-text bg-gradient-to-b from-[#333333] to-[#999999] mb-8">
          <h1 className="text-[26px] font-bold leading-[26.12px] bg-clip-text bg-gradient-to-b from-[#333333] to-[#999999]">
            Hello Again!
          </h1>
          <p className="bg-clip-text text-[18px] font-normal text-transparent">Welcome Back</p>
        </div>
        <div className="relative mb-2">
          <FaEnvelope className="absolute text-gray-400 transform -translate-y-1/2 left-5 top-1/2" />
          <input
            type="email"
            placeholder="Email address"
            {...register('email')}
            className={`w-[307px] h-[60px] px-[45px] py-[18px] border rounded-[30px] bg-white focus:outline-none focus:ring-0 placeholder:text-black/25 ${
              errors.email ? 'border-red-500' : 'border-[#EEEEEE]'
            }`}
            aria-invalid={errors.email ? 'true' : 'false'}
          />
        </div>
        <p className="text-sm text-red-500">{errors.email?.message}</p>

        <div className="relative mb-2">
          <FaLock className="absolute text-gray-400 transform -translate-y-1/2 left-5 top-1/2" />
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            {...register('password')}
            className={`w-[307px] h-[60px] px-[45px] pr-[70px] py-[18px] border rounded-[30px] bg-white focus:outline-none focus:ring-0 placeholder:text-black/25 ${
              errors.password ? 'border-red-500' : 'border-[#EEEEEE]'
            }`}
            aria-invalid={errors.password ? 'true' : 'false'}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute text-gray-400 transform -translate-y-1/2 right-5 top-1/2 hover:text-gray-600 focus:outline-none"
            aria-label={showPassword ? 'Hide password' : 'Show password'}>
            {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
          </button>
        </div>
        <p className="text-sm text-red-500">{errors.password?.message}</p>
        <div className="flex items-center gap-0 mb-2">
          <button
            type="submit"
            className="w-[307px] h-[57px] px-[26px] py-[18px] bg-[#000000] border border-[#000000] text-white rounded-[30px] opacity-100 flex items-center justify-center"
            disabled={isSubmitting}>
            {isSubmitting ? <BeatLoader color="#ffffff" size={10} /> : 'Login'}
          </button>
        </div>
        <div className="flex items-center justify-center gap-2 mt-6 mb-6">
          <hr className="w-[400px] border-0 border-t-2 border-[#F5F5F5] opacity-100" />
          <span className="text-[#000000] bg-white px-2">or</span>
          <hr className="w-[400px] border-0 border-t-2 border-[#F5F5F5] opacity-100" />
        </div>
        <div className="mb-2">
          <p className="text-center text-[14px] leading-[14.06px] bg-clip-text text-transparent bg-gradient-to-b from-[#333333] to-[#999999] mt-8">
            <Link to="/forgot-password">Forgot Password</Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default Login;
